import { useState, useEffect, useRef } from "react";

const PRESETS = [
  { label: "5 min", sec: 300 },
  { label: "10 min", sec: 600 },
  { label: "15 min", sec: 900 },
  { label: "20 min", sec: 1200 },
  { label: "30 min", sec: 1800 },
];

// Inject global styles once so the root and body fill the viewport
const globalStyle = `
  html, body, #root {
    height: 100%;
    margin: 0;
    padding: 0;
    overflow: hidden;
  }
`;

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return String(m).padStart(2, "0") + ":" + String(sec).padStart(2, "0");
}

function timerColor(s) {
  if (s <= 30) return "#A32D2D";
  if (s <= 60) return "#BA7517";
  return "inherit";
}

export default function TCGChessTimer() {
  const [screen, setScreen] = useState("setup");
  const [totalSeconds, setTotalSeconds] = useState(600);
  const [timeA, setTimeA] = useState(600);
  const [timeB, setTimeB] = useState(600);
  const [active, setActive] = useState("A");
  const [paused, setPaused] = useState(false);
  const [turn, setTurn] = useState(1);
  const [winner, setWinner] = useState(null);
  const intervalRef = useRef(null);

  // Inject global CSS for full-height layout
  useEffect(() => {
    const tag = document.createElement("style");
    tag.textContent = globalStyle;
    document.head.appendChild(tag);
    return () => document.head.removeChild(tag);
  }, []);

  useEffect(() => {
    if (screen !== "game" || paused) return;
    intervalRef.current = setInterval(() => {
      if (active === "A") {
        setTimeA((t) => {
          if (t <= 1) { endGame("B"); return 0; }
          return t - 1;
        });
      } else {
        setTimeB((t) => {
          if (t <= 1) { endGame("A"); return 0; }
          return t - 1;
        });
      }
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [screen, paused, active]);

  function endGame(w) {
    clearInterval(intervalRef.current);
    setWinner(w);
    setScreen("winner");
  }

  function startGame() {
    setTimeA(totalSeconds);
    setTimeB(totalSeconds);
    setActive("A");
    setPaused(false);
    setTurn(1);
    setWinner(null);
    setScreen("game");
  }

  function resetGame() {
    clearInterval(intervalRef.current);
    setScreen("setup");
  }

  function tap(who) {
    if (screen !== "game" || paused || active !== who) return;
    const next = who === "A" ? "B" : "A";
    setActive(next);
    if (next === "A") setTurn((t) => t + 1);
  }

  function togglePause() {
    if (screen !== "game") return;
    setPaused((p) => !p);
  }

  const s = {
    arena: {
      display: "flex",
      flexDirection: "column",
      height: "100dvh",           // dynamic viewport height — handles mobile browser chrome
      width: "100%",
      fontFamily: "system-ui, sans-serif",
      userSelect: "none",
      WebkitUserSelect: "none",
      position: "relative",
      background: "white",
      overflow: "hidden",
    },
    panel: (player, isActive, isPaused) => ({
      flex: 1,
      minHeight: 0,               // let flexbox shrink below content size
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "clamp(1rem, 4vw, 2rem)",
      cursor: "pointer",
      transition: "background 0.2s",
      position: "relative",
      gap: "clamp(6px, 1.5vh, 14px)",
      transform: player === "B" ? "rotate(180deg)" : "none",
      borderBottom: player === "B" ? "1px solid #e5e5e5" : "none",
      background: isActive && !isPaused ? "#f5f5f5" : "transparent",
      opacity: isPaused ? 0.75 : isActive ? 1 : 0.45,
      touchAction: "manipulation",
    }),
    label: {
      fontSize: "clamp(11px, 1.5vw, 14px)",
      fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#888",
    },
    timer: (t) => ({
      // Fluid font: scales between 48px on tiny screens up to 10vw on wide ones
      fontSize: "clamp(48px, 10vw, 96px)",
      fontWeight: 500,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.03em",
      color: timerColor(t),
      lineHeight: 1,
      fontFamily: "ui-monospace, monospace",
    }),
    pip: (on) => ({
      width: "clamp(6px, 1vw, 10px)",
      height: "clamp(6px, 1vw, 10px)",
      borderRadius: "50%",
      background: on ? "#1D9E75" : "#ccc",
      flexShrink: 0,
    }),
    hint: {
      fontSize: "clamp(11px, 1.5vw, 13px)",
      color: "#bbb",
      position: "absolute",
      bottom: "clamp(8px, 2vh, 16px)",
    },
    divider: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "clamp(8px, 2vw, 20px)",
      padding: "clamp(6px, 1.5vh, 10px) clamp(1rem, 4vw, 2rem)",
      borderTop: "0.5px solid #e5e5e5",
      borderBottom: "0.5px solid #e5e5e5",
      background: "white",
      flexShrink: 0,
      zIndex: 10,
    },
    btn: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: "clamp(12px, 1.5vw, 14px)",
      fontWeight: 500,
      padding: "clamp(6px, 1vh, 10px) clamp(12px, 2vw, 18px)",
      borderRadius: 8,
      border: "0.5px solid #ccc",
      background: "transparent",
      cursor: "pointer",
      touchAction: "manipulation",
      whiteSpace: "nowrap",
    },
    turnCount: {
      fontSize: "clamp(12px, 1.5vw, 14px)",
      color: "#888",
      minWidth: 52,
      textAlign: "center",
    },
    overlay: {
      position: "absolute",
      inset: 0,
      background: "white",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "clamp(16px, 3vh, 28px)",
      zIndex: 100,
      padding: "clamp(1rem, 4vw, 2rem)",
    },
    title: {
      fontSize: "clamp(18px, 3vw, 22px)",
      fontWeight: 500,
      color: "#111",
    },
    subtitle: {
      fontSize: "clamp(13px, 1.8vw, 15px)",
      color: "#888",
    },
    timeOptions: {
      display: "flex",
      gap: "clamp(6px, 1.5vw, 12px)",
      flexWrap: "wrap",
      justifyContent: "center",
    },
    timeBtn: (selected) => ({
      padding: "clamp(8px, 1.5vh, 12px) clamp(14px, 2.5vw, 22px)",
      borderRadius: 8,
      border: selected ? "1px solid #555" : "0.5px solid #ccc",
      background: selected ? "#f0f0f0" : "transparent",
      fontSize: "clamp(13px, 1.8vw, 15px)",
      fontWeight: 500,
      cursor: "pointer",
      touchAction: "manipulation",
    }),
    startBtn: {
      padding: "clamp(10px, 1.8vh, 14px) clamp(24px, 4vw, 36px)",
      borderRadius: 8,
      border: "none",
      background: "#111",
      color: "white",
      fontSize: "clamp(14px, 2vw, 16px)",
      fontWeight: 500,
      cursor: "pointer",
      touchAction: "manipulation",
    },
  };

  const aIsActive = active === "A" && screen === "game";
  const bIsActive = active === "B" && screen === "game";

  return (
    <div style={s.arena}>
      {/* Setup overlay */}
      {screen === "setup" && (
        <div style={s.overlay}>
          <p style={s.title}>TCG Chess Timer</p>
          <p style={s.subtitle}>Choose time per player</p>
          <div style={s.timeOptions}>
            {PRESETS.map((p) => (
              <button
                key={p.sec}
                style={s.timeBtn(totalSeconds === p.sec)}
                onClick={() => setTotalSeconds(p.sec)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button style={s.startBtn} onClick={startGame}>
            Start game
          </button>
        </div>
      )}

      {/* Winner overlay */}
      {screen === "winner" && (
        <div style={s.overlay}>
          <p style={{ fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 500, color: "#111" }}>
            Player {winner} wins!
          </p>
          <p style={s.subtitle}>Time ran out</p>
          <button style={s.btn} onClick={resetGame}>
            ↺ Play again
          </button>
        </div>
      )}

      {/* Player B (top, rotated) */}
      <div
        style={s.panel("B", bIsActive, paused)}
        onClick={() => tap("B")}
        role="button"
        aria-label="Player B — tap to end turn"
      >
        <div style={s.pip(bIsActive && !paused)} />
        <p style={s.label}>Player B</p>
        <p style={s.timer(timeB)}>{fmt(timeB)}</p>
        <p style={{ ...s.hint, top: "clamp(8px, 2vh, 16px)", bottom: "auto" }}>tap to end turn</p>
      </div>

      {/* Divider controls */}
      <div style={s.divider}>
        <button style={s.btn} onClick={togglePause}>
          {paused ? "▶ Resume" : "⏸ Pause"}
        </button>
        <span style={s.turnCount}>Turn {turn}</span>
        <button style={s.btn} onClick={resetGame}>
          ↺ Reset
        </button>
      </div>

      {/* Player A (bottom) */}
      <div
        style={s.panel("A", aIsActive, paused)}
        onClick={() => tap("A")}
        role="button"
        aria-label="Player A — tap to end turn"
      >
        <div style={s.pip(aIsActive && !paused)} />
        <p style={s.label}>Player A</p>
        <p style={s.timer(timeA)}>{fmt(timeA)}</p>
        <p style={s.hint}>tap to end turn</p>
      </div>
    </div>
  );
}
